import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ref, get, set, remove } from 'firebase/database';
import { database } from '../../firebase';
import { UserForm } from '../components/UserForm';

const EditableTestResults = ({ userId, results, setResults }) => {
  const [editRow, setEditRow] = useState(null);
  const [editValue, setEditValue] = useState('');

  const handleSave = async (resultId) => {
    const resultRef = ref(database, `completedTests/${userId}/${resultId}/score`);
    await set(resultRef, parseInt(editValue, 10));
    setResults(results.map(r => r.id === resultId ? { ...r, score: editValue } : r));
    setEditRow(null);
  };

  const handleDelete = async (resultId) => {
    if (!confirm('Are you sure you want to delete this test result?')) return;
    const resultRef = ref(database, `completedTests/${userId}/${resultId}`);
    await remove(resultRef);
    setResults(results.filter(r => r.id !== resultId));
  };

  if (results.length === 0) {
    return <p className="text-gray-500">No test results found for this user.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Test Name</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {results.map(result => (
            <tr key={result.id}>
              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{result.name}</td>
              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                {editRow === result.id ? (
                  <input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} className="w-20 border-gray-300 rounded" />
                ) : (
                  result.score
                )}
              </td>
              <td className="px-4 py-2 whitespace-nowrap text-sm font-medium space-x-2">
                {editRow === result.id ? (
                  <button onClick={() => handleSave(result.id)} className="text-green-600 hover:text-green-900">Save</button>
                ) : (
                  <button onClick={() => { setEditRow(result.id); setEditValue(result.score); }} className="text-blue-600 hover:text-blue-900">Edit</button>
                )}
                <button onClick={() => handleDelete(result.id)} className="text-red-600 hover:text-red-900">Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  const isCreateMode = userId === 'create';

  useEffect(() => {
    if (isCreateMode) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const userRef = ref(database, `users/${userId}`);
        const userSnapshot = await get(userRef);

        if (userSnapshot.exists()) {
          const userData = userSnapshot.val();
          setUser(userData);

          if (userData.tests && userData.tests.completed) {
            setTestResults(Object.entries(userData.tests.completed).map(([id, data]) => ({ id, ...data })));
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, isCreateMode]);

  const handleSaveUser = async (formData) => {
    if (isCreateMode) {
      // Create new user
      try {
        const { auth } = await import('../../firebase');
        const { createUserWithEmailAndPassword } = await import('firebase/auth');
        const newUserCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        const newUser = newUserCredential.user;
        const userRef = ref(database, `users/${newUser.uid}`);
        await set(userRef, {
          ...formData,
          uid: newUser.uid,
          createdAt: new Date().toISOString(),
        });
        navigate('/admin');
      } catch (error) {
        console.error('Error creating user:', error);
        alert('Failed to create user. See console for details.');
      }
    } else {
      // Update existing user
      const userRef = ref(database, `users/${userId}`);
      await set(ref(database, `users/${userId}`), { ...user, ...formData });
      setUser({ ...user, ...formData });
      setIsEditing(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    const userRef = ref(database, `users/${userId}`);
    await remove(userRef);
    navigate('/admin');
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (isCreateMode) {
    return <UserForm user={null} onSave={handleSaveUser} onCancel={() => navigate('/admin')} />
  }

  if (!user) {
    return <div>User not found</div>;
  }

  if (isEditing) {
    return <UserForm user={user} onSave={handleSaveUser} onCancel={() => setIsEditing(false)} />
  }

  return (
    <div className="p-6">
      <button onClick={() => navigate('/admin')} className="mb-4">Back to User List</button>
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex justify-between items-start">
          <h2 className="text-2xl font-bold mb-4">{user.name}</h2>
          <div className="space-x-2">
            <button onClick={() => setIsEditing(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md">Edit</button>
            <button onClick={handleDeleteUser} className="px-4 py-2 bg-red-600 text-white rounded-md">Delete</button>
          </div>
        </div>
        <div>
          <p><strong>Email:</strong> {user.email}</p>
          <p><strong>Class:</strong> {user.class}</p>
          <p><strong>Exam:</strong> {user.exam}</p>
          <p><strong>Target Year:</strong> {user.targetYear}</p>
          <p><strong>Phone:</strong> {user.phone}</p>
        </div>
        <div className="mt-6">
          <h3 className="text-lg font-semibold">Test Results</h3>
          <EditableTestResults userId={userId} results={testResults} setResults={setTestResults} />
        </div>
      </div>
    </div>
  );
};